import { useRef } from 'react';
import Icon, { Icons } from '@/components/elements/Icon';
import { Address } from 'nftx.js';
import { SwapType } from '@/domain/swap';
import Price from '@/components/elements/Price';
import FormattedEther from '@/components/elements/FormattedEther';
import { parseEther } from 'viem';
import VaultIcon from '@/components/elements/VaultIcon';
import { useTranslations } from 'use-intl';

export default function Erc20Input({
  source,
  network,
  type,
  amount,
  amountMax,
  ethAmount,
  token,
  vaultId,
  canChangeToken,
  dyadType,
  symbol: tokenSymbol,
  fetching,
  onChange,
  onSearchClick,
}: {
  source: 'wallet' | 'vault';
  network: number;
  type: SwapType;
  token: Address | '';
  vaultId?: string;
  amount: `${number}` | '';
  amountMax?: `${number}`;
  ethAmount?: bigint;
  dyadType: SwapType;
  canChangeToken: boolean;
  symbol: string;
  fetching: boolean;
  onChange: (value: `${number}`) => void;
  onSearchClick: () => void;
}) {
  const t = useTranslations('Swap');
  const inputRef = useRef<HTMLInputElement>(null);

  const symbol = type === 'ETH' ? 'ETH' : tokenSymbol;

  const handleChange = (v: string) => {
    const isValidNumber = v.match(/^[0-9]*\.{0,1}[0-9]*$/);
    if (isValidNumber) {
      onChange(v as `${number}`);
    }
  };

  return (
    <div>
      <div className="flex items-center">
        <button
          type="button"
          className={`py-2 px-2 flex items-center text-sm uppercase font-bold border-2 border-gray-300 rounded-lg text-gray-800 ${
            canChangeToken
              ? 'cursor-pointer hover:border-gray-400 hover:bg-gray-100'
              : 'cursor-default'
          }`}
          onClick={() => canChangeToken && onSearchClick()}
        >
          {type === 'ETH' ? (
            <span className="bg-blue-500 w-5 h-5 mr-2 rounded-full flex-none flex items-center justify-center">
              <Icon name={Icons.ETH} className="text-white" size={16} />
            </span>
          ) : symbol && vaultId ? (
            <VaultIcon
              vaultId={vaultId}
              className="rounded-full h-5 w-5 mr-2 flex-none"
            />
          ) : null}
          <span>{symbol || 'Select token'}</span>
          {canChangeToken && (
            <Icon
              name={Icons.CHEVRON_DOWN}
              size={12}
              className="ml-2 flex-none"
            />
          )}
        </button>
        <div className="flex-1 flex items-end overflow-hidden relative">
          <input
            ref={inputRef}
            className={`font-sans text-2xl text-gray-800 text-right w-full bg-transparent outline-0 flex-1 border-0 focus:ring-0 pr-0 p-0 pl-4 truncate transition-opacity ${
              fetching ? 'opacity-0' : 'opacity-1'
            }`}
            value={fetching ? '' : amount}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="0"
            type={fetching ? 'text' : 'number'}
            disabled={dyadType === 'NFT'}
          />
          {fetching && (
            <Icon
              name={Icons.SPINNER_GAP}
              className="animate animate-spin text-gray-400 absolute right-0 top-2"
            />
          )}
        </div>
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-sm text-gray-400 font-medium">
          {t.rich('balance', {
            count: (
              <FormattedEther
                value={parseEther(amountMax || '0')}
                key="amountMax"
              />
            ) as any,
          })}
          {source === 'wallet' && dyadType !== 'NFT' && (
            <button
              type="button"
              onClick={() => handleChange(amountMax || '0')}
              className="font-bold text-pink-500 hover:underline uppercase ml-1"
            >
              {t('max')}
            </button>
          )}
        </span>
        <span className="text-gray-400 text-sm font-medium ml-auto">
          {!!ethAmount &&
            t.rich('value', {
              value: (
                <Price currency="USD" value={ethAmount} key="ethAmount" />
              ) as any,
            })}
        </span>
      </div>
    </div>
  );
}
